import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Popover, Text } from "@mantine/core"
import { IconAlertTriangle, IconCircleCheck, IconLoader2, IconWifi } from "@tabler/icons-react"
import { useState } from "react"
import classes from "./ConnectionPill.module.css"

/** Health classification — mirrors useRecordingHealthCheck's THealthStatus. */
export type TConnectionHealth = "nominal" | "warning" | "error"

interface IProps {
  /** Aggregate recording-health status. */
  status: TConnectionHealth
  /** True while the live-transcription WebSocket is in its reconnect loop. */
  wsReconnecting: boolean
  /** Outstanding health issues — shown in the popover detail. */
  issues: string[]
}

/**
 * A focusable connection/health chip for the Lexicanon recording topbar.
 *
 * Unlike the legacy HealthIndicator (a hover-only Tooltip on a colour dot), this
 * pairs colour with an icon AND a text label, and exposes the detail on a
 * focusable button via a Popover so keyboard + screen-reader users can reach it.
 * Reconnecting takes visual precedence over the resting health status so the
 * "transcript froze but audio is safe" hiccup is never silent.
 */
export function ConnectionPill({ status, wsReconnecting, issues }: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const [opened, setOpened] = useState(false)

  // Capture death (tab freeze / muted mic) outranks reconnecting — reconnecting
  // used to claim "nothing is lost" while Android Chrome had already stopped
  // producing audio.
  const tone: "reconnecting" | TConnectionHealth =
    status === "error" ? "error" : wsReconnecting ? "reconnecting" : status

  const label =
    tone === "reconnecting"
      ? i18n._(msg`Reconnecting`)
      : tone === "error"
        ? i18n._(msg`Connection issue`)
        : tone === "warning"
          ? i18n._(msg`Degraded`)
          : i18n._(msg`Connected`)

  const icon =
    tone === "reconnecting" ? (
      <IconLoader2 size={13} className={classes.spin} aria-hidden="true" />
    ) : tone === "error" || tone === "warning" ? (
      <IconAlertTriangle size={13} aria-hidden="true" />
    ) : status === "nominal" ? (
      <IconCircleCheck size={13} aria-hidden="true" />
    ) : (
      <IconWifi size={13} aria-hidden="true" />
    )

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" withArrow shadow="md" width={260}>
      <Popover.Target>
        <button
          type="button"
          className={classes.pill}
          data-tone={tone}
          onClick={() => setOpened((o) => !o)}
          onMouseEnter={() => setOpened(true)}
          onMouseLeave={() => setOpened(false)}
          onFocus={() => setOpened(true)}
          onBlur={() => setOpened(false)}
          aria-label={i18n._(msg`Connection status: ${label}`)}
        >
          {icon}
          <span className={classes.label}>{label}</span>
        </button>
      </Popover.Target>
      <Popover.Dropdown>
        {tone === "reconnecting" ? (
          <Text size="xs">
            <Trans>Reconnecting live transcription. Your audio is still recording and nothing is lost.</Trans>
          </Text>
        ) : issues.length > 0 ? (
          <Text size="xs">{issues.join(" · ")}</Text>
        ) : (
          <Text size="xs">
            <Trans>All systems nominal. Your audio is recording safely.</Trans>
          </Text>
        )}
      </Popover.Dropdown>
    </Popover>
  )
}
